import { Component, type ErrorInfo, type ReactNode } from 'react'
import { config } from '../config'
import { isStaleChunkError, recoverFromStaleChunk } from '../lib/recoverStaleChunk'

type Props = { children: ReactNode }

type State = { hasError: boolean; message: string; recovering: boolean }

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, message: '', recovering: false }
  }

  static getDerivedStateFromError(err: Error): State {
    if (isStaleChunkError(err)) {
      return { hasError: true, recovering: true, message: 'Updating to the latest version…' }
    }
    const isDev = config.isDev
    const message = isDev
      ? (err.message || 'Something went wrong')
      : 'Please try reloading the page. If the problem continues, contact support.'
    return { hasError: true, recovering: false, message }
  }

  componentDidCatch(err: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', err, info.componentStack)
    if (isStaleChunkError(err)) {
      void recoverFromStaleChunk()
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            background: '#0a0f1a',
            color: '#e8eaed',
            textAlign: 'center',
          }}
        >
          <h1 style={{ fontSize: '1.25rem', marginBottom: 8 }}>
            {this.state.recovering ? 'Updating…' : 'Something went wrong'}
          </h1>
          <p style={{ opacity: 0.85, marginBottom: 16 }}>{this.state.message}</p>
          {!this.state.recovering && (
          <button
            type="button"
            onClick={() => {
              void recoverFromStaleChunk().then((did) => {
                if (!did) window.location.reload()
              })
            }}
            style={{
              padding: '10px 20px',
              borderRadius: 8,
              border: '1px solid rgba(123,147,219,0.5)',
              background: 'rgba(123,147,219,0.15)',
              color: '#e8eaed',
              cursor: 'pointer',
            }}
          >
            Reload page
          </button>
          )}
        </div>
      )
    }
    return this.props.children
  }
}
